import { BranchesOutlined, SmileOutlined } from "@ant-design/icons";
import { ROUTES } from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import {
  useFetchAppraisalPermission,
  useFetchOrgs,
  getIsCompanyPoliciesPurchaseBlocked,
  useGetAuth,
  useGetPermissions,
  useGetPermissionsEachPage,
} from "@lhd/hooks";
import { Company, ContractStatusEnums } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { get } from "lodash";
import { hasEventFormManagementPermission } from "@lhd/features/EventForms/permissions";
import {
  AiOutlineBranches,
  AiOutlineMedicineBox,
  AiOutlineUpSquare,
  AiOutlineUsergroupAdd,
  AiOutlineAudit,
  AiOutlineFolderOpen,
  AiOutlineProfile,
} from "react-icons/ai";
import { CiRuler } from "react-icons/ci";
import {
  FaFileContract,
  FaRegClock,
  FaRegHandshake,
  FaFileInvoiceDollar,
  FaGamepad,
} from "react-icons/fa";
import {
  MdOutlineSettingsSuggest,
  MdGroups,
  MdOutlineVideoCameraFront,
  MdOutlineAddChart,
  MdOutlineDynamicForm,
} from "react-icons/md";
import {
  DocumentIcon,
  SettingIcon2,
  OrgIcon,
  DocumentCheckIcon,
  DocumentRing,
} from "@lhd/assets";
import { GrDocumentConfig } from "react-icons/gr";
import { BiColumns } from "react-icons/bi";
import { RiFileSearchLine, RiOrganizationChart } from "react-icons/ri";
import { TbClipboardList } from "react-icons/tb";
import { VscOrganization } from "react-icons/vsc";
import { isMobile, isTablet } from "react-device-detect";
import { useSelector } from "react-redux";
import { CONTRACT_STATUS_ACCEPTED } from "./constants";
import { TOOLTIP_MESSAGES } from "./constants";

export const useNavItem = () => {
  const auth = useSelector(user);

  // const workers: any = get(auth, "workers[0]", null);
  const { data: orgsData } = useFetchOrgs();
  const { data: appraisalPermissionData } = useFetchAppraisalPermission();

  const isAdmin = get(auth, "is_admin", false);
  const currentContractStatus = get(auth, "current_contract_status", "");
  const isCompanyManament = get(auth, "is_on_company_admin", false);
  const canChangeUser = get(auth, "can_change_user", false) || isAdmin;
  const currentCompanyLogin = auth?.companies?.find(
    (i: Company) => Number(i?.id) === Number(getCompanyId()),
  );
  const isRootCompany = currentCompanyLogin?.is_root_company;
  const isMobileOrTablet = isMobile || isTablet;
  const isContractStatusAccepted = CONTRACT_STATUS_ACCEPTED.includes(
    currentContractStatus as typeof CONTRACT_STATUS_ACCEPTED[number],
  );
  const hasCompanyAdminContext =
    isAdmin &&
    (currentCompanyLogin?.is_admin || isRootCompany) &&
    isContractStatusAccepted;
  const { isUserResigned } = useGetAuth();

  const permission = useGetPermissions();
  const permissionEachPage = useGetPermissionsEachPage();
  const canManageEventForms = hasEventFormManagementPermission(auth);

  const subBusinessItems = [
    // {
    //   ...(fulPermission && {
    //     id: 1,
    //     title: "取引先マスタ",
    //     icon: <MdOutlineBusinessCenter />,
    //     navigate: ROUTES.CUSTOMER_LIST,
    //     mb: "0rem",
    //   }),
    // },
    // {
    //   ...(fulPermission && {
    //     id: 2,
    //     title: "プロジェクト管理",
    //     icon: <VscProject />,
    //     navigate: ROUTES.PROJECT_MANAGE,
    //     mb: "0rem",
    //   }),
    // },
    // {
    //   ...(fulPermission && {
    //     id: 3,
    //     title: "Bizシート",
    //     icon: <AiOutlineAppstore />,
    //     navigate: ROUTES.APPS,
    //     mb: "0rem",
    //   }),
    // },
    // {
    //   id: 1,
    //   title: "取引先マスタ（β版）",
    //   icon: <MdOutlineBusinessCenter size={20} />,
    //   navigate: ROUTES.CUSTOMER_LIST,
    //   mb: "0rem",
    // },
    // {
    //   id: 2,
    //   title: "プロジェクト管理（β版）",
    //   icon: <VscProject size={20} />,
    //   navigate: ROUTES.PROJECT_MANAGE,
    //   mb: "0rem",
    // },
    // {
    //   id: 3,
    //   title: "Bizシート",
    //   icon: <AiOutlineAppstore size={20} />,
    //   navigate: ROUTES.APPS,
    //   mb: "0rem",
    // },
    // {
    //   id: 4,
    //   title: "Bizシート権限",
    //   icon: <AiOutlineAppstore />,
    //   navigate: ROUTES.SETTING_PRIVILEGE,
    //   mb: "0rem",
    // },
  ].filter((i) => Object.keys(i).length > 0);

  const isContractSigned =
    currentCompanyLogin?.contract_status === ContractStatusEnums.OFFICIAL ||
    currentCompanyLogin?.contract_status === ContractStatusEnums.TRIAL ||
    currentCompanyLogin?.contract_status === ContractStatusEnums.UN_CONTRACTED;

  const hasAppraisalPermission =
    (appraisalPermissionData?.general_appraisal_delete || auth?.is_admin) &&
    isContractSigned;

  const hasCompanyPoliciesPermission =
    permissionEachPage[ROUTES.COMPANY_POLICIES]?.create ||
    permissionEachPage[ROUTES.COMPANY_POLICIES]?.edit ||
    permissionEachPage[ROUTES.COMPANY_POLICIES]?.delete ||
    permissionEachPage[ROUTES.COMPANY_POLICIES]?.view ||
    permissionEachPage[ROUTES.COMPANY_POLICIES]?.all;

  const isForcedNotPurchasedCompany =
    currentContractStatus === ContractStatusEnums.NEW_CONTRACT ||
    currentContractStatus === ContractStatusEnums.CONTRACT_TERMINATED;
  const isCompanyPoliciesPurchaseBlocked = getIsCompanyPoliciesPurchaseBlocked(
    currentContractStatus,
    permission.companyPolicyEnabled,
  );

  const companyPoliciesDisabledTooltipMessage = isCompanyPoliciesPurchaseBlocked
    ? TOOLTIP_MESSAGES.NOT_PURCHASE
    : !hasCompanyPoliciesPermission
    ? TOOLTIP_MESSAGES.NO_PERMISSION
    : undefined;

  const hasRolePermissionsPermission =
    permissionEachPage[ROUTES.ROLE_PERMISSIONS]?.create ||
    permissionEachPage[ROUTES.ROLE_PERMISSIONS]?.edit ||
    permissionEachPage[ROUTES.ROLE_PERMISSIONS]?.delete ||
    permissionEachPage[ROUTES.ROLE_PERMISSIONS]?.view ||
    permissionEachPage[ROUTES.ROLE_PERMISSIONS]?.all;

  const hasBusinessManualsPermission =
    permissionEachPage[ROUTES.BUSINESS_MANUALS]?.create ||
    permissionEachPage[ROUTES.BUSINESS_MANUALS]?.edit ||
    permissionEachPage[ROUTES.BUSINESS_MANUALS]?.delete ||
    permissionEachPage[ROUTES.BUSINESS_MANUALS]?.view ||
    permissionEachPage[ROUTES.BUSINESS_MANUALS]?.all;

  const handleGetDisabledMsgRolePermissions = () => {
    if (
      isForcedNotPurchasedCompany ||
      !auth?.worker_application_usage?.role_permission
    ) {
      return TOOLTIP_MESSAGES.NOT_PURCHASE;
    }

    return TOOLTIP_MESSAGES.NO_PERMISSION;
  };

  const subPersonalItems = [
    {
      id: 1,
      title: "組織図",
      icon: <RiFileSearchLine size={20} />,
      navigate: ROUTES.PERSONNEL,
      mb: "0rem",
      hasNoPermission: !canChangeUser,
    },
    {
      id: 3,
      title: "社員一覧",
      icon: <SmileOutlined size={20} />,
      navigate: ROUTES.WORKERS,
      mb: "0rem",
    },
  ];

  const subOrgItems = [
    // {
    //   ...((fulPermission ||
    //     permission?.includes(PERMISSIONS["company_management"])) && {
    //     id: 1,
    //     title: "→会社",
    //     icon: <LoginIcon />,
    //     navigate: ROUTES.COMPANY_CONFIGURATION,
    //     mb: "0rem",
    //   }),
    // },
    {
      id: 2,
      title: "支店",
      icon: <BranchesOutlined size={20} />,
      navigate: ROUTES.BRANCH,
      mb: "0rem",
    },
    {
      id: 3,
      title: "組織",
      icon: <RiOrganizationChart size={20} />,
      navigate: ROUTES.ORGANIZATIONS,
      mb: "0rem",
    },
    {
      id: 4,
      title: "部門",
      icon: <VscOrganization size={20} />,
      navigate: ROUTES.DEPARTMENTS,
      mb: "0rem",
    },
    {
      id: 5,
      title: "部署",
      icon: <AiOutlineBranches size={20} />,
      navigate: ROUTES.DIVISIONS,
      mb: "0rem",
    },
  ];

  const personnelItem = [
    {
      id: 1,
      title: "組織図",
      icon: <RiFileSearchLine size={20} />,
      navigate: ROUTES.PERSONNEL,
      mb: "0rem",
    },
  ];

  const subOperationItems = [
    {
      id: 1,
      title: "契約会社",
      icon: (
        <DocumentIcon
          color={
            !permission[ROUTES.COMPANIES] || isUserResigned ? "#fffff" : "black"
          }
        />
      ),
      navigate: ROUTES.COMPANIES,
      mb: "0rem",
      hasNoPermission: !permission[ROUTES.COMPANIES] || isUserResigned,
    },
    {
      id: 2,
      title: "設定",
      icon: <SettingIcon2 />,
      navigate: ROUTES.ADMIN_CONFIG,
      mb: "0rem",
      hasNoPermission: !permission[ROUTES.ADMIN_CONFIG] || isUserResigned,
    },
  ];

  const appStoreItems = [
    {
      id: 1,
      title: "Bizシートストア管理",
      icon: <TbClipboardList size={20} />,
      navigate: ROUTES.PERSONNEL,
      mb: "0rem",
    },
  ];

  const subAppStoreItems = [
    {
      id: 1,
      title: "ストアカテゴリ",
      icon: (
        <OrgIcon
          color={
            !permission[ROUTES.APP_CATEGORIES] || isUserResigned
              ? "#fffff"
              : "black"
          }
        />
      ),
      navigate: ROUTES.APP_CATEGORIES,
      mb: "0rem",
      hasNoPermission: !permission[ROUTES.APP_CATEGORIES] || isUserResigned,
    },
    {
      id: 2,
      title: "ストア承認管理",
      icon: (
        <DocumentCheckIcon
          color={
            !permission[ROUTES.APP_SUBMITTED_MANAGEMENT] || isUserResigned
              ? "#fffff"
              : "black"
          }
        />
      ),
      navigate: ROUTES.APP_SUBMITTED_MANAGEMENT,
      mb: "0rem",
      hasNoPermission:
        !permission[ROUTES.APP_SUBMITTED_MANAGEMENT] || isUserResigned,
    },
    {
      id: 3,
      title: "メンテナンス通知",
      icon: (
        <DocumentRing
          color={
            !permission[ROUTES.MAINTENANCE_NOTIFICATION] || isUserResigned
              ? "#fffff"
              : "black"
          }
        />
      ),
      navigate: ROUTES.MAINTENANCE_NOTIFICATION,
      mb: "0rem",
      hasNoPermission:
        !permission[ROUTES.MAINTENANCE_NOTIFICATION] || isUserResigned,
    },
  ];

  const subConfigurationItems = [
    // {
    //   ...(fulPermission && {
    //     id: 1,
    //     title: "組織マスタ管理",
    //     icon: <LoginIcon style={{ height: 16, width: 16 }} />,
    //     navigate: ROUTES.ORGANIZATION_MANAGEMENT,
    //     mb: "0.3rem",
    //   }),
    // },
    // {
    //   id: 1,
    //   title: "組織マスタ管理",
    //   icon: <AiOutlineUpSquare style={{ height: 16, width: 16 }} size={20} />,
    //   navigate: ROUTES.ORGANIZATION_MANAGEMENT,
    //   mb: "0.3rem",
    // },
    {
      id: 2,
      title: "役職一覧",
      icon: <AiOutlineUpSquare style={{ height: 16, width: 16 }} size={20} />,
      navigate: ROUTES.POSITIONS,
      mb: "0.3rem",
      hasNoPermission: !canChangeUser,
    },
    // {
    //   id: 3,
    //   title: "会社情報",
    //   icon: <AiOutlineUpSquare style={{ height: 16, width: 16 }} size={20} />,
    //   navigate: ROUTES.COMPANY_CONFIGURATION,
    //   mb: "0rem",
    // },
  ];

  const subManagementItems = [
    // {
    //   ...(fulPermission && {
    //     id: 1,
    //     title: "→経営層",
    //     icon: <LoginIcon />,
    //     navigate: ROUTES.MANAGEMENT,
    //     mb: "0.3rem",
    //   }),
    // },
    // {
    //   ...(fulPermission && {
    //     id: 2,
    //     title: "→組織",
    //     icon: <LoginIcon />,
    //     navigate: ROUTES.ORGANIZATIONS,
    //     mb: "0.3rem",
    //   }),
    // },
    // {
    //   ...(fulPermission && {
    //     id: 3,
    //     title: "→部門",
    //     icon: <LoginIcon />,
    //     navigate: ROUTES.DEPARTMENTS,
    //     mb: "0.3rem",
    //   }),
    // },
    ...(orgsData?.data || []).map((item: any) => {
      return {
        title: `${item.name}`,
        icon: <AiOutlineMedicineBox size={20} />,
        navigate: `${ROUTES.ORGS}/${item.id}`,
        mb: "0.3rem",
      };
    }),
  ];

  const subManualItems = [
    {
      id: 1,
      title: "給与明細利用の仕方",
      icon: <></>,
      navigate: ROUTES.MANAGEMENT,
      mb: "0.3rem",
    },
  ];

  const subPermissionsSalaryItems = [
    // {
    //   id: 1,
    //   title: "会社情報",
    //   icon: <RiBuildingLine size={20} />,
    //   navigate: ROUTES.COMPANY_CONFIGURATION,
    //   mb: "0rem",
    // },
    {
      id: 2,
      title: "組織マスタ管理",
      icon: <AiOutlineUpSquare style={{ height: 16, width: 16 }} size={20} />,
      navigate: ROUTES.ORGANIZATION_MANAGEMENT,
      mb: "0.3rem",
      hasNoPermission: !permission[ROUTES.ORGANIZATION_MANAGEMENT],
    },
    {
      id: 3,
      title: "権限設定",
      icon: <></>,
      navigate: ROUTES.PERMISSIONS_MANAGEMENT,
      mb: "0.3rem",
      hasNoPermission: !permission[ROUTES.PERMISSIONS_MANAGEMENT],
    },
  ];

  const subProjectManagerItems = [
    {
      id: 1,
      title: "プロジェクト一覧",
      icon: <></>,
      navigate: ROUTES.PROJECT_MANAGER_LIST,
      mb: "0rem",
    },
  ];

  const navItems = [
    {
      id: "tracking-time",
      icon: <FaRegClock size={20} />,
      title: "勤怠アプリ",
      navigate: ROUTES.TRACKING_TIME,
      mb: "0rem",
      groupType: "default",
      hasNoPermission: isUserResigned,
    },
    {
      id: "correlation-diagram",
      icon: <RiOrganizationChart size={20} />,
      title: "全機能相関図",
      navigate: ROUTES.CORRELATION_DIAGRAM,
      mb: "0rem",
      groupType: "default",
    },
    // ◆ Default items in the basic package - no charge
    // ■ Permission Management
    {
      id: 15,
      icon: <FaFileContract size={20} />,
      title: "契約管理",
      navigate: ROUTES.CONTRACT_MANAGEMENT,
      mb: "0rem",
      groupType: "default",
      hasNoPermission: isUserResigned,
    },
    {
      id: 18,
      icon: <GrDocumentConfig size={20} />,
      title: "権限管理",
      navigate: ROUTES.PERMISSION_SUMMARIES,
      mb: "0rem",
      groupType: "default",
      hasNoPermission: !isContractStatusAccepted || isUserResigned,
    },
    // ◇ Data / Master
    {
      id: "group-data-master",
      title: "◇ マスタ",
      isSubGroupHeader: true,
      groupType: "default",
      mb: "0rem",
      childrenTabs: [
        {
          id: 2,
          title: "人事・組織",
          icon: <MdGroups size={20} />,
          navigate: ROUTES.ANNUAL_ORG_SUMMARIES,
          mb: "0rem",
          hasNoPermission: !permission[ROUTES.ANNUAL_ORG_SUMMARIES],
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subCompanyConfigItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "default",
          subGroupType: "data-master",
        },
        {
          id: 14,
          title: "取引先情報",
          icon: <FaRegHandshake size={20} />,
          navigate: ROUTES.CUSTOMER_HISTORY_SUMMARIES,
          mb: "0rem",
          groupType: "default",
          subGroupType: "data-master",
          hasNoPermission: !permission[ROUTES.CUSTOMER_LIST] || isUserResigned,
        },
      ],
    },

    // ◇ Creative App
    {
      id: "group-creative-app",
      title: "◇ 創作アプリ",
      isSubGroupHeader: true,
      groupType: "default",
      mb: "0rem",
      childrenTabs: [
        // ・Bizシート (Biz Sheet)
        {
          id: 10,
          icon: <BiColumns size={20} />,
          title: "Bizシート",
          navigate: ROUTES.BIZ_SHEET_SUMMARIES,
          mb: "0rem",
          subAppItems: true,
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subCompanyConfigItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "default",
          subGroupType: "creative-app",
          hasNoPermission:
            !permission[ROUTES.APPS] ||
            !permission.defaultBasicPackage ||
            isUserResigned,
        },
        {
          id: "event-forms",
          icon: <MdOutlineDynamicForm size={20} />,
          title: "Bizフォーム",
          navigate: ROUTES.EVENT_FORMS,
          mb: "0rem",
          groupType: "default",
          subGroupType: "creative-app",
          hasNoPermission:
            !canManageEventForms ||
            !isAdmin ||
            !isContractStatusAccepted ||
            isUserResigned,
          disabledTooltipMessage: TOOLTIP_MESSAGES.NO_PERMISSION,
        },
      ],
    },

    // ◆ Paid applications
    // ◇ Core App
    {
      id: "group-core-app",
      title: "◇ 基幹アプリ",
      isSubGroupHeader: true,
      groupType: "paid",
      mb: "0rem",
      childrenTabs: [
        // ・Payroll / Payment Statement
        {
          id: 9,
          icon: <FaFileInvoiceDollar size={20} />,
          title: "給与明細・支払計算書",
          navigate: ROUTES.ANNUAL_SALARY_SUMMARIES,
          mb: "0rem",
          groupType: "default",
          subGroupType: "data-master",
          hasNoPermission: isUserResigned
            ? !permission[ROUTES.ANNUAL_SALARY_SUMMARIES]
            : !permission.salarySummariesAccess,
        },
        // ・Company Regulations / Work Rules
        {
          id: "company_policies",
          icon: <AiOutlineAudit size={20} />,
          title: "社内規定・就業規則",
          navigate: isMobileOrTablet
            ? ROUTES.COMPANY_POLICIES
            : ROUTES.COMPANY_POLICIES_CHART,
          mb: "0rem",
          subCompanyItems: false,
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "paid",
          subGroupType: "core-app",
          isDevelopment: false,
          hasNoPermission: isMobileOrTablet
            ? isCompanyPoliciesPurchaseBlocked ||
              !hasCompanyPoliciesPermission ||
              isUserResigned
            : isCompanyPoliciesPurchaseBlocked || isUserResigned,
          disabledTooltipMessage: isMobileOrTablet
            ? companyPoliciesDisabledTooltipMessage
            : undefined,
        },
        {
          id: "role_permissions",
          icon: <CiRuler size={22} />,
          title: "内部統制",
          navigate: ROUTES.BUSINESS_CONTROLS_CHART,
          mb: "0rem",
          groupType: "paid",
          subGroupType: "core-app",
          hasNoPermission: !permission.role_permissions || isUserResigned,
          disabledTooltipMessage: handleGetDisabledMsgRolePermissions(),
        },
        // {
        //   id: "business_manuals",
        //   icon: <AiOutlineProfile size={20} />,
        //   title: "業務マニュアル",
        //   navigate: ROUTES.BUSINESS_MANUALS,
        //   mb: "0rem",
        //   groupType: "paid",
        //   subGroupType: "core-app",
        //   hasNoPermission: !hasBusinessManualsPermission || isUserResigned,
        // },
        {
          id: 15,
          icon: <AiOutlineFolderOpen size={20} />,
          title: "プロジェクト管理",
          // iconActive: <Icon.Folder />,
          // iconNotActive: <Icon.Folder color="#666666" />,
          navigate: ROUTES.PROJECT_CHART,
          mb: "0rem",
          subProjectManagerItems: true,
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subCompanyConfigItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "paid",
          subGroupType: "core-app",
          hasNoPermission: !permission[ROUTES.PROJECT_MANAGE] || isUserResigned,
        },
        // ・E-Learning
        {
          id: "e-learning",
          icon: <MdOutlineVideoCameraFront size={20} />,
          title: "Eラーニング",
          // iconActive: <Icon.ElectronicLearning />,
          // iconNotActive: <Icon.ElectronicLearning color="#666666" />,
          navigate: isMobileOrTablet
            ? ROUTES.Electronic_Learning
            : ROUTES.ELECTRONIC_LEARNING_CHART,
          mb: "0rem",
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subCompanyConfigItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "paid",
          subGroupType: "core-app",
          hasNoPermission:
            !isContractSigned ||
            !auth?.worker_application_usage?.e_learning ||
            isUserResigned,
        },
        // ・Personnel Evaluation
        {
          id: 16,
          icon: <AiOutlineProfile size={20} />,
          title: "人事評価",
          // iconActive: <Icon.AppraisalActive />,
          // iconNotActive: <Icon.Appraisal />,
          navigate: ROUTES.APPRAISAL_SUMMARIES,
          mb: "0rem",
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subCompanyConfigItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "paid",
          subGroupType: "core-app",
          hasNoPermission: !permission.appraisal || isUserResigned,
        },
        // ・Budget / Actual Management / KPI
        {
          id: 11,
          icon: <BiColumns size={20} />,
          title: "予実管理・ＫＰＩ",
          // icon: <BiColumns size={20} />,
          navigate: ROUTES.KPI,
          mb: "0rem",
          groupType: "paid",
          subGroupType: "core-app",
          hasNoPermission:
            !isAdmin || !permission[ROUTES.KPI] || isUserResigned,
          disabledTooltipMessage: !isAdmin
            ? TOOLTIP_MESSAGES.NO_PERMISSION
            : !permission[ROUTES.KPI]
            ? TOOLTIP_MESSAGES.NOT_PURCHASE
            : undefined,
        },
      ],
    },

    {
      id: "group-game",
      title: "◇ ゲーム",
      isSubGroupHeader: true,
      groupType: "default",
      mb: "0rem",
      childrenTabs: [
        {
          id: "business-rpg",
          icon: <FaGamepad size={20} />,
          title: "RPG(ビジネス・FIRE)",
          navigate: ROUTES.BUSINESS_RPG,
          mb: "0rem",
          subSalaryItems: false,
          subBusinessItems: false,
          subPersonalItems: false,
          subOperationItems: false,
          subCompanyConfigItems: false,
          subAppStoreItems: false,
          subAppraisalItems: false,
          subOrganizationItems: false,
          groupType: "default",
          subGroupType: "game",
          dataTestId: "nav-business-rpg",
          hasNoPermission: isUserResigned,
        },
      ],
    },
  ].filter((i) => Object.keys(i).length > 0);

  return {
    auth,
    navItems,
    subBusinessItems,
    subPersonalItems,
    subOperationItems,
    subOrgItems,
    subConfigurationItems,
    // subAppraisalItems,
    personnelItem,
    subManagementItems,
    appStoreItems,
    subAppStoreItems,
    subManualItems,
    subPermissionsSalaryItems,
    subProjectManagerItems,
    canChangeUser,
  };
};
